/**
 * List of property websites that we support.
 *
 * To add a new property:
 * 1. Add it to typedef in `PropertyName`
 * 2. Add it to `propertyNames` array.
 * 3. Add a config for it in `themeBrandConfigs` array in `/src/common/theme/config.ts`.
 */

import { PropertyAddress, PropertyPhoneNumber, RegionProperty, SocialSharing } from 'src/api/generated/graph';

export const propertySlugs = [
  'aria',
  'beaurivage',
  'bellagio',
  'borgata',
  'delanolasvegas',
  'excalibur',
  'goldstrike',
  'luxor',
  'mandalaybay',
  'mgmgrand',
  'mgmresorts',
  'mirage',
  'mgmnationalharbor',
  'nomadlasvegas',
  'mgmnorthfieldpark',
  'newyorknewyork',
  'parkmgm',
  'signaturemgmgrand',
  'mgmspringfield',
  'vdara',
  'mgmgranddetroit',
] as const;

export type PropertySlug = typeof propertySlugs[number];

export interface PropertyConfig {
  id: string;
  regionId: string;
  regionName: string;
  websiteTitle: string;
  isMultiPropertyWebsite?: boolean;
  ticker: string;
}

// Corporate sort order is just used for testing
export const propertyConfigs: { [key in PropertySlug]: PropertyConfig & { corporateSortOrder?: string } } = {
  aria: {
    corporateSortOrder: '3',
    id: 'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'aria',
    websiteTitle: 'ARIA Resort & Casino',
  },
  beaurivage: {
    id: '13b178b0-8beb-43d5-af25-1738b7267e63',
    regionId: 'mississippi',
    regionName: 'Mississippi',
    ticker: 'beau',
    websiteTitle: 'Beau Rivage Resort & Casino',
  },
  bellagio: {
    corporateSortOrder: '13',
    id: '44e610ab-c209-4232-8bb4-51f7b9b13a75',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'bell',
    websiteTitle: 'Bellagio Hotel & Casino',
  },
  borgata: {
    id: '773000cc-468a-4d86-a38f-7ae78ecfa6aa',
    regionId: 'new-jersey',
    regionName: 'New Jersey',
    ticker: 'borg',
    websiteTitle: 'Borgata Hotel & Casino',
  },
  delanolasvegas: {
    corporateSortOrder: '8',
    id: '8bf670c2-3e89-412b-9372-6c87a215e442',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'dlno',
    websiteTitle: 'Delano Las Vegas',
  },
  excalibur: {
    corporateSortOrder: '2',
    id: 'f8d6a944-7816-412f-a39a-9a63aad26833',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'exca',
    websiteTitle: 'Excalibur Hotel & Casino',
  },
  goldstrike: {
    id: 'b35733d1-e027-4311-a350-965e535fb90a',
    regionId: 'mississippi',
    regionName: 'Mississippi',
    ticker: 'tnca',
    websiteTitle: 'Gold Strike Casino Resort',
  },
  luxor: {
    corporateSortOrder: '12',
    id: '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'luxr',
    websiteTitle: 'Luxor Hotel & Casino',
  },
  mandalaybay: {
    corporateSortOrder: '4',
    id: 'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'mbay',
    websiteTitle: 'Mandalay Bay',
  },
  mgmgrand: {
    corporateSortOrder: '10',
    id: '66964e2b-2550-4476-84c3-1a4c0c5c067f',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'mgmg',
    websiteTitle: 'MGM Grand Las Vegas',
  },
  mgmgranddetroit: {
    id: '4a65a92a-962b-433e-841c-37e18dc5d68d',
    regionId: 'detroit',
    regionName: 'Detroit',
    ticker: 'mgmgd',
    websiteTitle: 'MGM Grand Detroit',
  },
  mgmnationalharbor: {
    id: '0990fdce-7fc8-41b1-b8b6-9a25dce3db55',
    regionId: 'maryland',
    regionName: 'Maryland',
    ticker: 'mgmnh',
    websiteTitle: 'MGM National Harbor',
  },
  mgmnorthfieldpark: {
    id: '00ccdea6-7d78-410b-b0f0-5983bdf44cc2',
    regionId: 'ohio',
    regionName: 'Ohio',
    ticker: 'mgmnp',
    websiteTitle: 'MGM Northfield Park',
  },
  mgmresorts: {
    id: 'mgmresorts',
    isMultiPropertyWebsite: true,
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'mgmr',
    websiteTitle: 'MGM Resorts',
  },
  mgmspringfield: {
    id: '40b61feb-750a-45df-ae68-e23e6272b16b',
    regionId: 'massachusetts',
    regionName: 'Massachusetts',
    ticker: 'mgmsp',
    websiteTitle: 'MGM Springfield',
  },
  mirage: {
    corporateSortOrder: '7',
    id: 'a689885f-cba2-48e8-b8e0-1dff096b8835',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'mira',
    websiteTitle: 'The Mirage',
  },
  newyorknewyork: {
    corporateSortOrder: '5',
    id: 'dc00e77f-d6bb-4dd7-a8ea-dc33ee9675ad',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'nyny',
    websiteTitle: 'New York-New York Hotel & Casino',
  },
  nomadlasvegas: {
    corporateSortOrder: '15',
    id: '2159252c-60d3-47db-bbae-b1db6bb15072',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'nomd',
    websiteTitle: 'NoMad Las Vegas',
  },
  parkmgm: {
    corporateSortOrder: '6',
    id: 'bee81f88-286d-43dd-91b5-3917d9d62a68',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'pmgm',
    websiteTitle: 'Park MGM',
  },
  signaturemgmgrand: {
    corporateSortOrder: '20',
    id: '1f3ed672-3f8f-44d8-9215-81da3c845d83',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'sign',
    websiteTitle: 'The Signature at MGM Grand',
  },
  vdara: {
    corporateSortOrder: '9',
    id: '6c5cff3f-f01a-4f9b-87ab-8395ae8108db',
    regionId: 'las-vegas',
    regionName: 'Las Vegas',
    ticker: 'vdar',
    websiteTitle: 'Vdara Hotel & Spa',
  },
};

export function getPropertyConfigById(propertyId?: string) {
  return Object.values(propertyConfigs).find(p => p.id === propertyId);
}

export function getRegionIdByResortId(resortId?: string) {
  return getPropertyConfigById(resortId)?.regionId;
}

export function getPropertyIdByPropertySlug(propertySlug: PropertySlug) {
  return propertyConfigs[propertySlug].id;
}

export function getPropertyTickerByPropertySlug(propertySlug: PropertySlug) {
  return propertyConfigs[propertySlug].ticker;
}

export function getPropertyNameByResortId(resortId?: string) {
  return Object.keys(propertyConfigs).find(key => propertyConfigs[key as PropertySlug].id === resortId) as
    | PropertySlug
    | undefined;
}

export function getPropertyConfigsByRegionId(regionId?: PropertyConfig['regionId']) {
  return Object.values(propertyConfigs).filter(({ regionId: propertyRegionId }) => regionId === propertyRegionId);
}

export function getResortNameById(resortId?: string) {
  return Object.values(propertyConfigs).find(resortConfig => resortConfig.id === resortId)?.websiteTitle;
}

const regionProperties: Record<string, RegionProperty[]> = {
  detroit: [
    {
      id: '4a65a92a-962b-433e-841c-37e18dc5d68d',
      propertyDetails: {
        corporateSortOrder: '16',
        id: '4a65a92a-962b-433e-841c-37e18dc5d68d',
        images: {
          overview: {
            url: '/content/dam/MGM/mgm-grand-detroit/hotel/mgm-grand-detroit-hotel-valet.tif',
          },
        },
        maximumLengthOfStay: '10',
        maximumNumberOfGuests: '4',
        name: 'MGM Grand Detroit',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '4a65a92a-962b-433e-841c-37e18dc5d68d',
        relatedProperties: null,
        reopenDate: '09/01/2020',
        shortName: 'MGM Grand Detroit',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
  'las-vegas': [
    {
      id: '44e610ab-c209-4232-8bb4-51f7b9b13a75',
      propertyDetails: {
        corporateSortOrder: '1',
        id: '44e610ab-c209-4232-8bb4-51f7b9b13a75',
        images: {
          overview: {
            url: '/content/dam/MGM/bellagio/hotel/exterior/bellagio-hotel-exterior-south-angle-lagoon-close-up.tif',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '7',
        name: 'Bellagio Hotel & Casino',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '44e610ab-c209-4232-8bb4-51f7b9b13a75',
        relatedProperties: ['', ''],
        reopenDate: '07/01/2020',
        shortName: 'Bellagio',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
      propertyDetails: {
        corporateSortOrder: '2',
        id: 'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
        images: {
          overview: {
            url: '/content/dam/MGM/aria/hotel/aria/exterior/aria-hotel-exterior-dusk.tif',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'ARIA Resort & Casino',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
        relatedProperties: ['', ''],
        reopenDate: '07/02/2020',
        shortName: 'ARIA',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '6c5cff3f-f01a-4f9b-87ab-8395ae8108db',
      propertyDetails: {
        corporateSortOrder: '3',
        id: '6c5cff3f-f01a-4f9b-87ab-8395ae8108db',
        images: {
          overview: {
            url: '/content/dam/MGM/vdara/hotel/exterior/vdara-exterior-campus.tif',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'Vdara Hotel & Spa at ARIA Las Vegas',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '6c5cff3f-f01a-4f9b-87ab-8395ae8108db',
        relatedProperties: ['', ''],
        reopenDate: '07/03/2020',
        shortName: 'Vdara',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '66964e2b-2550-4476-84c3-1a4c0c5c067f',
      propertyDetails: {
        corporateSortOrder: '4',
        id: '66964e2b-2550-4476-84c3-1a4c0c5c067f',
        images: {
          overview: {
            url: '/content/dam/MGM/mgm-grand/hotel/mgm-grand/exterior/mgm-grand-hotel-mgm-grand-exterior-hero-shot-@2x.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '7',
        name: 'MGM Grand Las Vegas',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '66964e2b-2550-4476-84c3-1a4c0c5c067f',
        relatedProperties: [
          'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
          'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a',
          '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
        ],
        reopenDate: '07/04/2020',
        shortName: 'MGM Grand',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '1f3ed672-3f8f-44d8-9215-81da3c845d83',
      propertyDetails: {
        corporateSortOrder: '5',
        id: '1f3ed672-3f8f-44d8-9215-81da3c845d83',
        images: {
          overview: {
            url: '/content/dam/MGM/signature/hotel/architecture/signature-architecture-hero-shot-night-horizontal.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '4',
        name: 'The Signature at MGM Grand',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '1f3ed672-3f8f-44d8-9215-81da3c845d83',
        relatedProperties: ['8bf670c2-3e89-412b-9372-6c87a215e442', '6c5cff3f-f01a-4f9b-87ab-8395ae8108db'],
        reopenDate: '07/05/2020',
        shortName: 'The Signature',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a',
      propertyDetails: {
        corporateSortOrder: '6',
        id: 'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a',
        images: {
          overview: {
            url: '/content/dam/MGM/mandalay-bay/hero-shots/mandalay-bay-architecture-hero-shot-day-wide-angle-beach-view.tif',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'Mandalay Bay',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a',
        relatedProperties: [
          '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
          '66964e2b-2550-4476-84c3-1a4c0c5c067f',
          'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
        ],
        reopenDate: '07/06/2020',
        shortName: 'Mandalay Bay',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '8bf670c2-3e89-412b-9372-6c87a215e442',
      propertyDetails: {
        corporateSortOrder: '7',
        id: '8bf670c2-3e89-412b-9372-6c87a215e442',
        images: {
          overview: {
            url: '/content/dam/MGM/delano-las-vegas/hotel/architecture/delano-las-vegas-architecture-tower-sunset-palm-trees-panel.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '5',
        name: 'Delano Las Vegas',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '8bf670c2-3e89-412b-9372-6c87a215e442',
        relatedProperties: ['1f3ed672-3f8f-44d8-9215-81da3c845d83', '6c5cff3f-f01a-4f9b-87ab-8395ae8108db'],
        reopenDate: '07/07/2020',
        shortName: 'Delano Las Vegas',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'bee81f88-286d-43dd-91b5-3917d9d62a68',
      propertyDetails: {
        corporateSortOrder: '8',
        id: 'bee81f88-286d-43dd-91b5-3917d9d62a68',
        images: {
          overview: {
            url: '/content/dam/MGM/park-mgm/hotel/park-mgm-hotel-lobby.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '0',
        name: 'Park MGM',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'bee81f88-286d-43dd-91b5-3917d9d62a68',
        relatedProperties: [
          '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
          '66964e2b-2550-4476-84c3-1a4c0c5c067f',
          'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
        ],
        reopenDate: '07/08/2020',
        shortName: 'Park MGM',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '2159252c-60d3-47db-bbae-b1db6bb15072',
      propertyDetails: {
        corporateSortOrder: '9',
        id: '2159252c-60d3-47db-bbae-b1db6bb15072',
        images: {
          overview: {
            url: '/content/dam/MGM/nomad/hotel/nomad-hotel-double-beds-with-art-collection.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'NoMad Las Vegas',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '2159252c-60d3-47db-bbae-b1db6bb15072',
        relatedProperties: [
          'e2704b04-d515-45b0-8afd-4fa1424ff0a8',
          '44e610ab-c209-4232-8bb4-51f7b9b13a75',
          '6c5cff3f-f01a-4f9b-87ab-8395ae8108db',
        ],
        reopenDate: '07/09/2020',
        shortName: 'NoMad Las Vegas',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'a689885f-cba2-48e8-b8e0-1dff096b8835',
      propertyDetails: {
        corporateSortOrder: '10',
        id: 'a689885f-cba2-48e8-b8e0-1dff096b8835',
        images: {
          overview: {
            url: '/content/dam/MGM/the-mirage/hotel/exterior/mirage-venue-daytime-hero.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'The Mirage',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'a689885f-cba2-48e8-b8e0-1dff096b8835',
        relatedProperties: ['', '', ''],
        reopenDate: '07/10/2020',
        shortName: 'The Mirage',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'dc00e77f-d6bb-4dd7-a8ea-dc33ee9675ad',
      propertyDetails: {
        corporateSortOrder: '11',
        id: 'dc00e77f-d6bb-4dd7-a8ea-dc33ee9675ad',
        images: {
          overview: {
            url: '/content/dam/MGM/new-york-new-york/architecture/exterior/skyline/new-york-new-york-skyline-angle-blue-background.tif',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '5',
        name: 'New York-New York Hotel & Casino',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'dc00e77f-d6bb-4dd7-a8ea-dc33ee9675ad',
        relatedProperties: ['', '', ''],
        reopenDate: '07/11/2020',
        shortName: 'New York-New York',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
      propertyDetails: {
        corporateSortOrder: '12',
        id: '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
        images: {
          overview: {
            url: '/content/dam/MGM/luxor/hotel/pyramid-exterior/luxor-hotel-pyramid-night-exterior-strip-view.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '6',
        name: 'Luxor Hotel & Casino',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
        relatedProperties: ['f8d6a944-7816-412f-a39a-9a63aad26833', 'e0f70eb3-7e27-4c33-8bcd-f30bf3b1103a'],
        reopenDate: '07/12/2020',
        shortName: 'Luxor',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'f8d6a944-7816-412f-a39a-9a63aad26833',
      propertyDetails: {
        corporateSortOrder: '13',
        id: 'f8d6a944-7816-412f-a39a-9a63aad26833',
        images: {
          overview: {
            url: '/content/dam/MGM/excalibur/hotel/exterior/excalibur-hotel-cropped-night-time-exterior-hero-shot.jpg',
          },
        },
        maximumLengthOfStay: '14',
        maximumNumberOfGuests: '4',
        name: 'Excalibur Hotel & Casino',
        path: '',

        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'f8d6a944-7816-412f-a39a-9a63aad26833',
        relatedProperties: [
          '607c07e7-3e31-4e4c-a4e1-f55dca66fea2',
          'bee81f88-286d-43dd-91b5-3917d9d62a68',
          '66964e2b-2550-4476-84c3-1a4c0c5c067f',
        ],
        reopenDate: '07/13/2020',
        shortName: 'Excalibur',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
  maryland: [
    {
      id: '0990fdce-7fc8-41b1-b8b6-9a25dce3db55',
      propertyDetails: {
        corporateSortOrder: '19',
        id: '0990fdce-7fc8-41b1-b8b6-9a25dce3db55',
        images: {
          overview: {
            url: '/content/dam/MGM/mgm-national-harbor/architecture/exterior/mgm-national-harbor-architecture-aerial-two.jpg',
          },
        },
        maximumLengthOfStay: '7',
        maximumNumberOfGuests: '4',
        name: 'MGM National Harbor',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '0990fdce-7fc8-41b1-b8b6-9a25dce3db55',
        relatedProperties: null,
        reopenDate: '06/29/2020',
        shortName: 'MGM National Harbor',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
  massachusetts: [
    {
      id: '40b61feb-750a-45df-ae68-e23e6272b16b',
      propertyDetails: {
        corporateSortOrder: '23',
        id: '40b61feb-750a-45df-ae68-e23e6272b16b',
        images: {
          overview: {
            url: '/content/dam/MGM/mgm-springfield/hotel/exterior/mgm-springfield-hotel-hero-shot-with-cars-driving.jpg',
          },
        },
        maximumLengthOfStay: '7',
        maximumNumberOfGuests: '4',
        name: 'MGM Springfield',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '40b61feb-750a-45df-ae68-e23e6272b16b',
        relatedProperties: null,
        reopenDate: '08/01/2020',
        shortName: 'MGM Springfield',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
  mississippi: [
    {
      id: '13b178b0-8beb-43d5-af25-1738b7267e63',
      propertyDetails: {
        corporateSortOrder: '14',
        id: '13b178b0-8beb-43d5-af25-1738b7267e63',
        images: {
          overview: {
            url: '/content/dam/MGM/beau-rivage/Architecture/exterior/beau-rivage-exterior-hero-evening-new.jpg',
          },
        },
        maximumLengthOfStay: '10',
        maximumNumberOfGuests: '4',
        name: 'Beau Rivage Resort & Casino',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '13b178b0-8beb-43d5-af25-1738b7267e63',
        relatedProperties: null,
        reopenDate: '06/01/2020',
        shortName: 'Beau Rivage',
        socialSharing: '' as SocialSharing,
      },
    },
    {
      id: 'b35733d1-e027-4311-a350-965e535fb90a',
      propertyDetails: {
        corporateSortOrder: '15',
        id: 'b35733d1-e027-4311-a350-965e535fb90a',
        images: {
          overview: {
            url: '/content/dam/MGM/gold-strike/casino/gold-strike-casino-hero-shot-night-2.tif',
          },
        },
        maximumLengthOfStay: '10',
        maximumNumberOfGuests: '4',
        name: 'Gold Strike Casino Resort',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: 'b35733d1-e027-4311-a350-965e535fb90a',
        relatedProperties: null,
        reopenDate: '05/25/2020',
        shortName: 'Gold Strike',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
  'new-jersey': [
    {
      id: '773000cc-468a-4d86-a38f-7ae78ecfa6aa',
      propertyDetails: {
        corporateSortOrder: '22',
        id: '773000cc-468a-4d86-a38f-7ae78ecfa6aa',
        images: {
          overview: {
            url: '/content/dam/MGM/borgata/hotel/architecture/borgata-hotel-building-exterior-dusk.tif',
          },
        },
        maximumLengthOfStay: '10',
        maximumNumberOfGuests: '4',
        name: 'Borgata Hotel Casino & Spa',
        path: '',
        phoneNumber: '' as PropertyPhoneNumber,
        propertyAddress: '' as PropertyAddress,
        propertyId: '773000cc-468a-4d86-a38f-7ae78ecfa6aa',
        relatedProperties: null,
        reopenDate: null,
        shortName: 'Borgata',
        socialSharing: '' as SocialSharing,
      },
    },
  ],
};

export const getRegionProperties = () => {
  return regionProperties;
};

export const getRegionPropertiesByName = (name: string) => {
  return regionProperties[name] ?? [];
};
